import {
  storeDepartmentSchema,
  storeArticleSchema,
  type CreateStoreDepartmentDto,
  type CreateStoreArticleDto,
  type ReorderStoreDepartmentsDto,
  type StoreDepartment,
  type StoreArticle,
  type UpdateStoreDepartmentDto,
  type UpdateStoreArticleDto,
} from '@mrrmlab/shared-types';
import { z } from 'zod';
import type { HttpClient } from './http';

const departmentsArraySchema = z.array(storeDepartmentSchema);
const articlesArraySchema = z.array(storeArticleSchema);

export class StoreDepartmentsResource {
  constructor(private readonly http: HttpClient) {}

  list(storeId: string, signal?: AbortSignal): Promise<StoreDepartment[]> {
    return this.http.request({
      method: 'GET',
      path: `/stores/${encodeURIComponent(storeId)}/departments`,
      responseSchema: departmentsArraySchema,
      signal,
    });
  }

  create(storeId: string, dto: CreateStoreDepartmentDto, signal?: AbortSignal): Promise<StoreDepartment> {
    return this.http.request({
      method: 'POST',
      path: `/stores/${encodeURIComponent(storeId)}/departments`,
      body: dto,
      responseSchema: storeDepartmentSchema,
      signal,
    });
  }

  update(storeId: string, id: string, dto: UpdateStoreDepartmentDto, signal?: AbortSignal): Promise<StoreDepartment> {
    return this.http.request({
      method: 'PATCH',
      path: `/stores/${encodeURIComponent(storeId)}/departments/${encodeURIComponent(id)}`,
      body: dto,
      responseSchema: storeDepartmentSchema,
      signal,
    });
  }

  delete(storeId: string, id: string, signal?: AbortSignal): Promise<void> {
    return this.http.request({
      method: 'DELETE',
      path: `/stores/${encodeURIComponent(storeId)}/departments/${encodeURIComponent(id)}`,
      expectEmpty: true,
      signal,
    });
  }

  reorder(storeId: string, dto: ReorderStoreDepartmentsDto, signal?: AbortSignal): Promise<void> {
    return this.http.request({
      method: 'POST',
      path: `/stores/${encodeURIComponent(storeId)}/departments/reorder`,
      body: dto,
      expectEmpty: true,
      signal,
    });
  }
}

export class StoreArticlesResource {
  constructor(private readonly http: HttpClient) {}

  list(storeId: string, signal?: AbortSignal): Promise<StoreArticle[]> {
    return this.http.request({
      method: 'GET',
      path: `/stores/${encodeURIComponent(storeId)}/articles`,
      responseSchema: articlesArraySchema,
      signal,
    });
  }

  create(storeId: string, dto: CreateStoreArticleDto, signal?: AbortSignal): Promise<StoreArticle> {
    return this.http.request({
      method: 'POST',
      path: `/stores/${encodeURIComponent(storeId)}/articles`,
      body: dto,
      responseSchema: storeArticleSchema,
      signal,
    });
  }

  update(storeId: string, id: string, dto: UpdateStoreArticleDto, signal?: AbortSignal): Promise<StoreArticle> {
    return this.http.request({
      method: 'PATCH',
      path: `/stores/${encodeURIComponent(storeId)}/articles/${encodeURIComponent(id)}`,
      body: dto,
      responseSchema: storeArticleSchema,
      signal,
    });
  }

  delete(storeId: string, id: string, signal?: AbortSignal): Promise<void> {
    return this.http.request({
      method: 'DELETE',
      path: `/stores/${encodeURIComponent(storeId)}/articles/${encodeURIComponent(id)}`,
      expectEmpty: true,
      signal,
    });
  }
}
